import { getRootContainerProps, getRootContainerPropsDep } from "@client/content/lib/helper";
import { type ModuleApiInfo } from "@client/content/selectors";
import { type RootContainerProps } from "@client/content/types";
import { FormRadioButtonGroup, type RadioOptionItem } from "@client/controls/Inputs/RadioButtonGroup/component";
import { useI18n } from "@common/hooks/i18n";
import { sort } from "@common/lib/array";
import { type I18n } from "@common/lib/i18n";
import { identity } from "lodash";
import * as React from "react";
import { type IRef, withOnSubmit } from "../../hooks";
import { type Checkout } from "../../types";
import ParametersProvider from "../ParametersProvider";
import CheckoutPaymentAuthorizeNetCreditCard from "./AuthorizeNet/component";
import CheckoutPaymentNmiCreditCard from "./Nmi/component";
import CheckoutPaymentStripeCreditCard from "./Stripe/CreditCard/component";
import CheckoutPaymentWallet from "./Wallet/component";

export type PaymentMethodBaseProps = RootContainerProps & {
  apiInfo: ModuleApiInfo;

  integrationId: string | null;
  fieldStyle: React.CSSProperties;
  labelStyle: React.CSSProperties;
  paymentMethodId: string | null;

  billingShipingInfo: Partial<Checkout.BillingInformation>;
};

export type MapProps = RootContainerProps & {
  accounts: Checkout.AccountOption[];
  amount: number;
  apiInfo: ModuleApiInfo;
  billingShipingInfo: Partial<Checkout.BillingInformation>;
  fieldStyle: React.CSSProperties;
  currencyCode: string;
  labelStyle: React.CSSProperties;
  paymentMethods: Checkout.PaymentMethod[];
  hasSubscriptionItems: boolean;
};
export interface ActionProps {}
export type Props = ActionProps & MapProps;

const getPaymentMethodInfo = (i18n: I18n, x: Checkout.Api.PaymentIntegratedAppKey): string | undefined => {
  switch (x) {
    case "Flutterwave":
      return i18n.infoFlutterwavePaymentMethod;
    case "PaypalExpressCheckout":
      return i18n.infoPaypalExpressCheckoutPaymentMethod;
    case "AddPay":
      return i18n.infoAddPayCheckoutPaymentMethod;
    case "PayAtOnline":
      return i18n.infoPayAtOnlinePaymentMethod;
    case "Mollie":
      return i18n.infoMolliePaymentMethod;
    case "Softycomp":
      return i18n.infoSoftycompPaymentMethod;
    default:
      return;
  }
};

export default (props: Props) => {
  const i18n = useI18n();

  const [paymentMethodId, setPaymentMethodId] = React.useState<string | null>(
    (props.paymentMethods.find((x) => x.integratedAppTypeKey === "CreditCard") ?? props.paymentMethods[0])?.id ?? null
  );

  React.useEffect(() => {
    if (!props.paymentMethods.some((x) => x.id === paymentMethodId)) {
      setPaymentMethodId(
        (props.paymentMethods.find((x) => x.integratedAppTypeKey === "CreditCard") ?? props.paymentMethods[0])?.id ??
          null
      );
    }
  }, [props.paymentMethods, setPaymentMethodId]);

  const ref = withOnSubmit(identity);

  React.useImperativeHandle<any, IRef>(
    ref,
    () => ({
      getFinalValues: () =>
        Promise.resolve({
          paymentMethodId
        }),
      validate: async () => (paymentMethodId ? null : i18n.errorRequiredField)
    }),
    [ref, paymentMethodId]
  );

  const paymentMethod = React.useMemo(
    () => props.paymentMethods.find((x) => x.id === paymentMethodId),
    [props.paymentMethods, paymentMethodId]
  );

  const paymentMethodProps = React.useMemo<PaymentMethodBaseProps>(
    () => ({
      ...getRootContainerProps(props),
      apiInfo: props.apiInfo,
      fieldStyle: props.fieldStyle,

      labelStyle: props.labelStyle,
      integrationId: paymentMethod?.integrationId ?? null,
      billingShipingInfo: props.billingShipingInfo,
      paymentMethodId: paymentMethod?.id ?? null
    }),
    [
      ...getRootContainerPropsDep(props),
      paymentMethod?.id,
      paymentMethod?.integrationId,
      props.apiInfo,
      props.fieldStyle,
      i18n,
      props.labelStyle,
      props.billingShipingInfo
    ]
  );

  const typeOptions = React.useMemo((): RadioOptionItem[] => {
    return sort(props.paymentMethods, { key: "name" })
      .filter((x) => (!props.accounts.length && x.appKey !== "Wallets") || props.accounts.length)
      .map<RadioOptionItem>((x) => ({
        label: x.name,
        value: x.id,
        image: x.integratedAppImagePath
      }));
  }, [i18n, props.paymentMethods, props.accounts.length]);

  return (
    <div className="payment">
      <h4>{i18n.payment}</h4>

      {!!paymentMethod ? (
        <>
          <FormRadioButtonGroup
            onChange={setPaymentMethodId}
            options={typeOptions}
            orientation="vertical"
            value={paymentMethod.id}
          >
            {(item: RadioOptionItem) => {
              const pm = props.paymentMethods.find((x) => x.id === item.value)!;

              if (pm.integratedAppKey === "Authorize") {
                return <CheckoutPaymentAuthorizeNetCreditCard {...paymentMethodProps} />;
              }

              if (pm.integratedAppKey === "NMI") {
                return (
                  <ParametersProvider apiInfo={props.apiInfo} integrationId={pm.integrationId!} shouldRenderImmediately>
                    {({ parameters }) => (
                      <CheckoutPaymentNmiCreditCard
                        {...paymentMethodProps}
                        amount={props.amount}
                        currencyCode={props.currencyCode}
                        parameters={parameters}
                      />
                    )}
                  </ParametersProvider>
                );
              }

              if (pm.integratedAppKey === "Stripe") {
                return (
                  <ParametersProvider apiInfo={props.apiInfo} integrationId={pm.integrationId!}>
                    {({ parameters }) => (
                      <CheckoutPaymentStripeCreditCard
                        {...paymentMethodProps}
                        amount={props.amount}
                        hasSubscriptionItems={props.hasSubscriptionItems}
                        currencyCode={props.currencyCode}
                        parameters={parameters}
                      />
                    )}
                  </ParametersProvider>
                );
              }

              const paymentMethodInfo = getPaymentMethodInfo(i18n, pm.integratedAppKey);
              if (paymentMethodInfo) {
                return (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: paymentMethodInfo
                    }}
                    style={{ textAlign: "center" }}
                  />
                );
              }

              if (pm.appKey === "Wallets") {
                return (
                  <CheckoutPaymentWallet
                    {...getRootContainerProps(props)}
                    accounts={props.accounts}
                    amount={props.amount}
                    currencyCode={props.currencyCode}
                  />
                );
              }

              if (pm.description) {
                return (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: pm.description
                    }}
                    style={{ textAlign: "center" }}
                  />
                );
              }
            }}
          </FormRadioButtonGroup>
        </>
      ) : (
        <small className="error field_error">{i18n.errorNoPaymentMethod}</small>
      )}
    </div>
  );
};
